import { INSTITUTIONAL_TYPES } from './constants';

export function computeFeatures(brokerData, priceData, brokerTags) {
  const brokerBySymbol = groupBy(brokerData, 'symbol');
  const priceBySymbol = groupBy(priceData, 'symbol');
  const results = [];

  for (const symbol of Object.keys(priceBySymbol)) {
    const prices = priceBySymbol[symbol].sort((a, b) => cmpDate(a.date, b.date));
    const brokerRows = (brokerBySymbol[symbol] || []).sort((a, b) => cmpDate(a.date, b.date));
    if (!prices.length) continue;

    const latestPrice = prices[prices.length - 1];
    const dailyData = buildDailyFeatures(prices, brokerRows, brokerTags);
    if (!dailyData.length) continue;

    const latest = dailyData[dailyData.length - 1];
    results.push({ symbol, price: latestPrice.close, ...latest, dailyData });
  }
  return results;
}

function groupBy(arr, key) {
  const map = {};
  arr.forEach(item => {
    const k = item[key];
    if (!map[k]) map[k] = [];
    map[k].push(item);
  });
  return map;
}

function cmpDate(a, b) {
  return new Date(a) - new Date(b);
}

function getBrokerType(broker, brokerTags) {
  if (brokerTags[broker]) return brokerTags[broker];
  const lower = broker.toLowerCase();
  for (const [name, type] of Object.entries(brokerTags)) {
    if (lower.includes(name.toLowerCase()) || name.toLowerCase().includes(lower)) return type;
  }
  return 'OTHER';
}

function buildDailyFeatures(prices, brokerRows, brokerTags) {
  const dates = [...new Set(prices.map(p => p.date))].sort((a, b) => cmpDate(a, b));
  const brokerByDate = groupBy(brokerRows, 'date');
  const raw = [];

  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    const priceRow = prices.find(p => p.date === date);
    const dayBrokers = brokerByDate[date] || [];

    let instNet = 0, retailNet = 0, totalBuyVol = 0, totalSellVol = 0, totalValue = 0;

    dayBrokers.forEach(row => {
      const type = getBrokerType(row.broker, brokerTags);
      const netShares = row.netShares ?? (row.buyVol - row.sellVol);
      if (INSTITUTIONAL_TYPES.includes(type)) instNet += netShares;
      else if (type === 'RETAIL') retailNet += netShares;
      totalBuyVol += row.buyVol || 0;
      totalSellVol += row.sellVol || 0;
      totalValue += row.totalValue || 0;
    });

    const prevClose = i > 0 ? (prices.find(p => p.date === dates[i - 1])?.close || 0) : (priceRow?.open || 0);
    const priceChange = priceRow ? priceRow.close - prevClose : 0;
    const range = priceRow ? priceRow.high - priceRow.low : 0;

    raw.push({
      date,
      close: priceRow?.close || 0,
      open: priceRow?.open || 0,
      high: priceRow?.high || 0,
      low: priceRow?.low || 0,
      volume: priceRow?.volume || 0,
      value: totalValue || priceRow?.value || 0,
      instNet,
      retailNet,
      totalBuyVol,
      totalSellVol,
      totalValue: totalValue || priceRow?.value || 0,
      priceChange,
      range,
    });
  }

  // Compute rolling features
  return raw.map((day, i) => {
    const slice = (n) => raw.slice(Math.max(0, i - n + 1), i + 1);
    const prev3 = slice(3);
    const prev5 = slice(5);
    const prev10 = slice(10);

    const accumulation3d = prev3.reduce((s, d) => s + d.instNet, 0);
    const accumulation5d = prev5.reduce((s, d) => s + d.instNet, 0);
    const accumulation10d = prev10.reduce((s, d) => s + d.instNet, 0);

    // Absorption: heavy selling but price holds due to institutional buying
    const absorptionDays = prev5.filter(d =>
      d.totalSellVol > d.totalBuyVol * 0.6 && d.priceChange >= 0 && d.instNet > 0
    );
    const absorptionScore = prev5.length > 0 ? absorptionDays.length / prev5.length : 0;

    // Divergence: institutions buying while retail sells
    const divergenceDays = prev5.filter(d => d.instNet > 0 && d.retailNet < 0);
    const divergenceScore = prev5.length > 0 ? divergenceDays.length / prev5.length : 0;

    // Acceleration: change in institutional buying intensity
    const prevSlice = raw.slice(Math.max(0, i - 5), Math.max(0, i - 2));
    const prevAccum = prevSlice.reduce((s, d) => s + d.instNet, 0);
    let accelerationScore = 0;
    if (Math.abs(prevAccum) > 0) {
      accelerationScore = (accumulation3d - prevAccum) / Math.abs(prevAccum);
    } else {
      accelerationScore = accumulation3d > 0 ? 0.5 : accumulation3d < 0 ? -0.5 : 0;
    }
    accelerationScore = Math.max(-1, Math.min(1, accelerationScore));

    // Compression: volatility contraction
    const recentRanges = prev3.map(d => d.range);
    const histRanges = prev10.map(d => d.range);
    const avgRecent = avg(recentRanges);
    const avgHist = avg(histRanges);
    const compressionScore = avgHist > 0 ? Math.max(0, Math.min(1, 1 - avgRecent / avgHist)) : 0;

    // Seller ratio
    const recentBuy = prev5.reduce((s, d) => s + d.totalBuyVol, 0);
    const recentSell = prev5.reduce((s, d) => s + d.totalSellVol, 0);
    const sellerRatio = recentBuy > 0 ? recentSell / recentBuy : 0;

    // Liquidity score (based on 5-day avg value, 500k PHP threshold)
    const avg5dValue = prev5.reduce((s, d) => s + d.totalValue, 0) / Math.max(prev5.length, 1);
    const liquidityScore = Math.min(1, avg5dValue / 500000);

    // Distribution risk: institutional selling + retail chasing
    const distDays = prev5.filter(d => d.instNet < 0 && d.retailNet > 0);
    const distributionRiskScore = prev5.length > 0 ? distDays.length / prev5.length : 0;

    // Estimated institutional cost
    const instBuyDays = prev10.filter(d => d.instNet > 0);
    const estimatedInstitutionalCost = instBuyDays.length > 0
      ? instBuyDays.reduce((s, d) => s + d.close, 0) / instBuyDays.length
      : null;

    return {
      ...day,
      accumulation3d,
      accumulation5d,
      accumulation10d,
      absorptionScore,
      divergenceScore,
      accelerationScore,
      compressionScore,
      sellerRatio,
      liquidityScore,
      distributionRiskScore,
      estimatedInstitutionalCost,
    };
  });
}

function avg(arr) {
  return arr.length > 0 ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;
}
